import * as THREE from 'three/webgpu';
import type {EntityPatch} from '../../history/types';
import {SketchGroup} from '../../mesh/sketch';
import type {Viewer} from '../../viewer/Viewer';
import {ViewerMode} from '../../viewer/ViewerMode';
import {ownsObject3DGeometry} from '../../utils/scene-object/ownsObject3DGeometry';
import {SketchEntitiesTransformGeometryService} from '../entities-transform/SketchEntitiesTransformGeometryService';
import {SketchEntitiesTransformSelectionService} from '../entities-transform/SketchEntitiesTransformSelectionService';
import {SketchPlanePointerIntersectionResolver} from '../entities-transform/SketchPlanePointerIntersectionResolver';
import {SketchReferencedEntityCopyConstraintPolicy} from '../entities-transform/SketchReferencedEntityCopyConstraintPolicy';
import {SketchDimensionClipboardService} from './SketchDimensionClipboardService';

/**
 * 草图内容剪贴板控制器。
 *
 * 设计说明：
 * 1. 控制器复用草图实体变换选择服务和几何复制服务，不在快捷键链路重新实现实体识别与关系复制；
 * 2. 剪贴板保存脱离场景的实体快照与尺寸领域快照，源状态后续变化不会污染已经复制的内容；
 * 3. 粘贴只使用鼠标射线与当前草图平面的自由交点，不调用点捕捉管理器；
 * 4. 副本平移期间临时卸载复制域关系，确保位置直接写入且不执行几何约束计算。
 */
export class SketchEntityClipboardController {
    /** Viewer 门面。 */
    private readonly viewer: Viewer;
    /** 草图实体选择归一服务。 */
    private readonly selectionService: SketchEntitiesTransformSelectionService;
    /** 草图实体复制与变换服务。 */
    private readonly geometryService: SketchEntitiesTransformGeometryService;
    /** 鼠标到草图平面的自由求交解析器。 */
    private readonly planePointerIntersectionResolver: SketchPlanePointerIntersectionResolver;
    /** 智能尺寸领域剪贴板服务。 */
    private readonly dimensionClipboardService: SketchDimensionClipboardService;
    /** 转换实体引用对象的通用复制约束策略。 */
    private readonly referencedEntityConstraintPolicy: SketchReferencedEntityCopyConstraintPolicy;
    /** 当前剪贴板快照所属的脱离场景草图组。 */
    private clipboardSnapshotGroup: SketchGroup | null = null;
    /** 剪贴板内保持用户选择语义的实体副本。 */
    private clipboardSelectedEntities: THREE.Object3D[] = [];
    /** 剪贴板实体集合的中心锚点。 */
    private readonly clipboardAnchor = new THREE.Vector3();
    /** 复制来源草图组 ID，用于限制剪贴板生命周期。 */
    private sourceSketchGroupId: string | null = null;

    /**
     * 创建草图实体剪贴板控制器。
     *
     * @param viewer Viewer 门面。
     * @param selectionService 可注入的实体选择归一服务。
     * @param geometryService 可注入的实体复制与变换服务。
     * @param planePointerIntersectionResolver 可注入的自由平面求交解析器。
     * @param dimensionClipboardService 可注入的智能尺寸领域剪贴板服务。
     * @param referencedEntityConstraintPolicy 可注入的转换实体引用约束复制策略。
     */
    public constructor(
        viewer: Viewer,
        selectionService = new SketchEntitiesTransformSelectionService(),
        geometryService = new SketchEntitiesTransformGeometryService(),
        planePointerIntersectionResolver = new SketchPlanePointerIntersectionResolver(),
        dimensionClipboardService = new SketchDimensionClipboardService(),
        referencedEntityConstraintPolicy = new SketchReferencedEntityCopyConstraintPolicy(),
    ) {
        this.viewer = viewer;
        this.selectionService = selectionService;
        this.geometryService = geometryService;
        this.planePointerIntersectionResolver = planePointerIntersectionResolver;
        this.dimensionClipboardService = dimensionClipboardService;
        this.referencedEntityConstraintPolicy = referencedEntityConstraintPolicy;
    }

    /**
     * 复制当前普通选择链路中选中的草图实体与显式选中尺寸。
     *
     * @returns 成功建立非空剪贴板快照时返回 true。
     * @throws 当剪贴板快照构建失败时，释放本次快照资源并原样重新抛出原异常。
     */
    public copySelectedEntities(): boolean {
        const activeSketchGroup = this.resolveActiveSketchGroup();
        if (this.viewer.getMode() !== ViewerMode.Sketch || !activeSketchGroup) {
            return false;
        }

        // 与 Delete 键一致，复制前置条件从 Viewer 当前选择集合开始，再按变换实体语义归一。
        const selectedEntities = this.selectionService.resolveTransformEntities(
            this.viewer.getSelectedObjects(),
            activeSketchGroup,
            {
                // 矩形角点等控制点只表达点选命中，不能继续提升成父级边或整个闭合轮廓。
                stopAtNonTransformableSketchPoint: true,
                // 槽口任意组成对象都表达整个槽口的复制意图，避免粘贴出散落线弧。
                promoteSlotComponentsToOwner: true,
            },
        );
        if (selectedEntities.length === 0) {
            return false;
        }

        const snapshotGroup = new SketchGroup({
            referencePlane: activeSketchGroup.referencePlane,
        });
        const selectedCloneMap = new Map<string, THREE.Object3D>();
        const persistentIdCloneMap = new Map<string, string>();
        const shouldCloneConstraint = this.referencedEntityConstraintPolicy
            .createClonePredicate(selectedEntities);

        try {
            this.geometryService.cloneEntities(
                selectedEntities,
                snapshotGroup,
                selectedCloneMap,
                persistentIdCloneMap,
                shouldCloneConstraint,
            );
            const snapshotSelectedEntities = selectedEntities
                .map((entity) => selectedCloneMap.get(entity.uuid))
                .filter((entity): entity is THREE.Object3D => !!entity);
            const anchor = this.geometryService.resolveEntitiesAnchor(snapshotSelectedEntities);
            if (!anchor || snapshotSelectedEntities.length === 0) {
                this.releaseSnapshotGroup(snapshotGroup);
                return false;
            }

            // 新快照完整建立后才替换旧剪贴板，避免复制失败时破坏上一次有效内容。
            this.clear();
            this.clipboardSnapshotGroup = snapshotGroup;
            this.clipboardSelectedEntities = snapshotSelectedEntities;
            this.clipboardAnchor.copy(anchor);
            this.sourceSketchGroupId = activeSketchGroup.uuid;

            const selectedDimensionSnapshots = this.viewer.getSelectedSketchDimensions?.()
                .filter((selection) => selection.sketchId === activeSketchGroup.persistentId)
                .map((selection) => selection.snapshot) ?? [];
            const allSketchSnapshots = this.viewer.sketchGroupManager?.dimensionRepository
                ?.listBySketch(activeSketchGroup.persistentId)
                .map((dimension) => dimension.toSnapshot()) ?? [];

            this.dimensionClipboardService.capture({
                selectedSnapshots: selectedDimensionSnapshots,
                allSketchSnapshots,
                persistentIdMap: persistentIdCloneMap,
            });
            return true;
        } catch (error) {
            this.releaseSnapshotGroup(snapshotGroup);
            throw error;
        }
    }

    /**
     * 把剪贴板实体粘贴到最后一次画布内鼠标位置。
     *
     * @returns 成功生成至少一个副本时返回 true。
     * @throws 当几何、尺寸或历史提交失败时，回滚本次已创建尺寸并原样重新抛出原异常。
     */
    public pasteAtLastPointerPosition(): boolean {
        const activeSketchGroup = this.resolveActiveSketchGroup();
        if (
            this.viewer.getMode() !== ViewerMode.Sketch
            || !activeSketchGroup
            || activeSketchGroup.uuid !== this.sourceSketchGroupId
            || this.clipboardSelectedEntities.length === 0
        ) {
            return false;
        }

        const pointerPosition = this.viewer.eventManager?.getLastPointerClientPosition() ?? null;
        if (!pointerPosition) {
            return false;
        }

        // 直接对当前草图参考面求交；这里刻意不读取 PointSnapManager，也不调用变换点捕捉解析器。
        const targetPoint = this.planePointerIntersectionResolver.resolve(
            this.viewer,
            activeSketchGroup.referencePlane,
            pointerPosition.clientX,
            pointerPosition.clientY,
        );
        if (!targetPoint) {
            return false;
        }

        const translation = targetPoint.clone().sub(this.clipboardAnchor);
        const translationMatrix = this.geometryService.createTranslationMatrix(translation);
        let createdDimensionIds: string[] = [];
        let pasteResult: {
            entities: THREE.Object3D[];
            dimensionIds: string[];
            dimensionPatches: EntityPatch[];
        };

        try {
            pasteResult = this.viewer.executeActiveSketchRuntimeHistoryTransaction({
                label: '复制草图实体',
                commandType: 'sketch.runtime.copy-entities',
                work: () => {
                    const selectedCloneMap = new Map<string, THREE.Object3D>();
                    const persistentIdCloneMap = new Map<string, string>();
                    this.geometryService.cloneEntities(
                        this.clipboardSelectedEntities,
                        activeSketchGroup,
                        selectedCloneMap,
                        persistentIdCloneMap,
                    );
                    const pastedEntities = this.clipboardSelectedEntities
                        .map((entity) => selectedCloneMap.get(entity.uuid))
                        .filter((entity): entity is THREE.Object3D => !!entity);
                    if (pastedEntities.length === 0) {
                        return {
                            entities: [] as THREE.Object3D[],
                            dimensionIds: [] as string[],
                            dimensionPatches: [] as EntityPatch[],
                        };
                    }

                    // 临时卸载副本关系后直写位置，关系实例仅在写入完成后恢复，不参与本次位置计算。
                    this.geometryService.applyMatrixToEntitiesWithoutConstraintSolve(
                        pastedEntities,
                        translationMatrix,
                    );

                    /**
                     * Object3D.worldToLocal 会原地改写传入向量；这里必须使用副本，
                     * 否则首次粘贴会污染剪贴板锚点，导致后续粘贴的世界平移和尺寸标注偏移错误。
                     */
                    const sourceAnchorLocal = activeSketchGroup.referencePlane.worldToLocal(
                        this.clipboardAnchor.clone(),
                    );
                    const targetAnchorLocal = activeSketchGroup.referencePlane.worldToLocal(
                        targetPoint.clone(),
                    );
                    const repository = this.viewer.sketchGroupManager?.dimensionRepository ?? null;
                    const dimensionPasteResult = repository
                        ? this.dimensionClipboardService.paste({
                            repository,
                            sketchId: activeSketchGroup.persistentId,
                            persistentIdMap: persistentIdCloneMap,
                            localTranslation: [
                                targetAnchorLocal.x - sourceAnchorLocal.x,
                                targetAnchorLocal.y - sourceAnchorLocal.y,
                            ],
                        })
                        : {dimensionIds: [], snapshots: [], patches: [] as EntityPatch[]};
                    createdDimensionIds = [...dimensionPasteResult.dimensionIds];

                    const changedObjectIds = pastedEntities.map((entity) => entity.uuid);
                    activeSketchGroup.markDefinitionDirty({
                        reason: 'sketch.runtime.copy-entities',
                        sourceObjectId: pastedEntities[0]?.uuid,
                        changedObjectIds,
                    });
                    activeSketchGroup.commitDefinitionChanges({
                        reason: 'sketch.runtime.copy-entities',
                        sourceObjectId: pastedEntities[0]?.uuid,
                        changedObjectIds,
                    });
                    this.viewer.syncSketchEditingVisualLayering();
                    return {
                        entities: pastedEntities,
                        dimensionIds: [...dimensionPasteResult.dimensionIds],
                        dimensionPatches: [...dimensionPasteResult.patches],
                    };
                },
                shouldCommit: (result) => result.entities.length > 0,
                resolveExtraPatches: (result) => result.dimensionPatches,
                rollbackExtraState: (result) => {
                    const repository = this.viewer.sketchGroupManager?.dimensionRepository ?? null;
                    result.dimensionIds.forEach((dimensionId) => {
                        repository?.removeDimensionForRestore(dimensionId);
                    });
                },
            });
        } catch (error) {
            // work 在尺寸落库后的任一步骤失败时，Viewer 只能恢复草图组，尺寸仓库需在此对称回滚。
            const repository = this.viewer.sketchGroupManager?.dimensionRepository ?? null;
            createdDimensionIds.forEach((dimensionId) => {
                repository?.removeDimensionForRestore(dimensionId);
            });
            throw error;
        }

        if (pasteResult.entities.length === 0) {
            return false;
        }

        // 粘贴完成后选中新副本，保持后续 Delete、再次 Copy 与属性面板行为一致。
        this.viewer.entitySelectionManager?.selectEntitiesProgrammatically(pasteResult.entities);
        this.viewer.selectSketchDimensionsProgrammatically?.(pasteResult.dimensionIds);
        this.viewer.requestRender();
        return true;
    }

    /**
     * 清空当前草图会话的剪贴板快照。
     */
    public clear(): void {
        if (this.clipboardSnapshotGroup) {
            this.releaseSnapshotGroup(this.clipboardSnapshotGroup);
        }
        this.clipboardSnapshotGroup = null;
        this.clipboardSelectedEntities = [];
        this.clipboardAnchor.set(0, 0, 0);
        this.sourceSketchGroupId = null;
        this.dimensionClipboardService.clear();
    }

    /**
     * 释放控制器持有的全部资源。
     */
    public dispose(): void {
        this.clear();
    }

    /**
     * 读取当前活动草图组。
     *
     * @returns 当前活动草图组；不存在时返回 null。
     */
    private resolveActiveSketchGroup(): SketchGroup | null {
        return this.viewer.sketchGroupManager?.activeSketchGroup ?? null;
    }

    /**
     * 释放脱离场景的剪贴板快照组。
     *
     * @param snapshotGroup 待释放的快照组。
     */
    private releaseSnapshotGroup(snapshotGroup: SketchGroup): void {
        const disposedGeometrySet = new Set<THREE.BufferGeometry>();
        snapshotGroup.traverse((object) => {
            const geometry = Reflect.get(object, 'geometry');
            /**
             * 快照内的直线等实体拥有独立 geometry，需要在快照替换时释放；
             * 草图控制点继承 THREE.Sprite，其 geometry 是 Three.js 模块级共享资源，
             * 必须先通过统一所有权策略排除，否则会销毁实时场景仍在使用的 WebGPU Buffer。
             */
            if (
                !ownsObject3DGeometry(object)
                || !(geometry instanceof THREE.BufferGeometry)
                || disposedGeometrySet.has(geometry)
            ) {
                return;
            }
            disposedGeometrySet.add(geometry);
            geometry.dispose();
        });

        // 材质由全局材质工厂共享，快照只释放自身几何和监听，不销毁共享材质。
        snapshotGroup.dispose();
        snapshotGroup.clear();
    }
}
